import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { useCall } from "@/api/commerce";
import { useMe } from "@/api/hooks";
import { useAssets, type Asset } from "@/api/service";
import type { Order } from "@/api/types";
import { RecordPicker, type PickerValue } from "@/components/crm/RecordPicker";
import { Icon } from "@/components/icons";
import { Alert, EmptyState, Modal, PageHeader, Spinner } from "@/components/ui";
import { shortDate } from "@/lib/format";
import { can } from "@/lib/permissions";
import { CoverBadge, NewTicketDialog, errorText } from "./common";

function AddMachine({ onClose, account }: { onClose: () => void; account?: PickerValue }) {
  const save = useCall<Asset>("assets");
  const [customer, setCustomer] = useState<PickerValue | null>(account ?? null);
  const [product, setProduct] = useState<PickerValue | null>(null);
  const [v, setV] = useState({ name: "", serial_number: "", installed_on: "", warranty_until: "", location: "" });
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof v) => (e: React.ChangeEvent<HTMLInputElement>) => setV({ ...v, [k]: e.target.value });
  return (
    <Modal title="Add a machine" onClose={onClose}>
      <form className="space-y-4" onSubmit={(e) => {
        e.preventDefault(); setError(null);
        save.mutateAsync({ path: "/assets", body: { account_id: customer?.id, product_id: product?.id ?? null, name: v.name || null, serial_number: v.serial_number, installed_on: v.installed_on || null, warranty_until: v.warranty_until || null, location: v.location || null } })
          .then(onClose).catch((err) => setError(errorText(err)));
      }}>
        {error && <Alert>{error}</Alert>}
        {!account && <RecordPicker label="Customer" endpoint="/accounts" value={customer} onChange={setCustomer} />}
        <RecordPicker label="Product" endpoint="/products" value={product} onChange={setProduct} />
        <div className="grid gap-4 sm:grid-cols-2">
          <div><label htmlFor="am-name" className="label">Name</label><input id="am-name" className="input" placeholder="Blank to use the product name" value={v.name} onChange={set("name")} /></div>
          <div><label htmlFor="am-serial" className="label">Serial number</label><input id="am-serial" className="input" value={v.serial_number} onChange={set("serial_number")} /></div>
          <div><label htmlFor="am-installed" className="label">Installed on</label><input id="am-installed" className="input" type="date" value={v.installed_on} onChange={set("installed_on")} /></div>
          <div><label htmlFor="am-warranty" className="label">Warranty until</label><input id="am-warranty" className="input" type="date" value={v.warranty_until} onChange={set("warranty_until")} />
            <p className="mt-1 text-xs text-muted">Blank: the standard warranty from the install date.</p></div>
          <div className="sm:col-span-2"><label htmlFor="am-location" className="label">Location</label><input id="am-location" className="input" placeholder="e.g. Plant 2, press shop" value={v.location} onChange={set("location")} /></div>
        </div>
        <div className="flex justify-end gap-2"><button type="button" className="btn-ghost" onClick={onClose}>Cancel</button><button type="submit" className="btn-primary" disabled={!customer || !v.serial_number.trim() || save.isPending}>Add machine</button></div>
      </form>
    </Modal>
  );
}

export function MachineTable({ items, onTicket }: { items: Asset[]; onTicket?: (a: Asset) => void }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-wide text-muted"><tr><th className="px-4 py-2">Machine</th><th className="px-3 py-2">Serial</th><th className="px-3 py-2">Customer</th><th className="px-3 py-2">Installed</th><th className="px-3 py-2">Cover today</th><th className="px-3 py-2">Open tickets</th><th /></tr></thead>
        <tbody className="divide-y divide-line">
          {items.map((a) => (
            <tr key={a.id} aria-label={a.serial_number}>
              <td className="px-4 py-2 font-medium">{a.name}{a.location && <span className="block text-xs font-normal text-muted">{a.location}</span>}</td>
              <td className="px-3 py-2 font-mono text-xs">{a.serial_number}</td>
              <td className="px-3 py-2"><Link className="hover:text-brand" to={`/accounts/${a.account.id}`}>{a.account.name}</Link></td>
              <td className="px-3 py-2 text-muted">{a.installed_on ? shortDate(a.installed_on) : "—"}</td>
              <td className="px-3 py-2">{a.coverage && <><CoverBadge c={a.coverage.kind} /><span className="block text-xs text-muted">{a.coverage.label}</span></>}</td>
              <td className="px-3 py-2">{a.open_tickets ? <Link className="text-brand hover:underline" to={`/service?asset=${a.id}`}>{a.open_tickets}</Link> : "—"}</td>
              <td className="px-3 py-2 text-right">{onTicket && <button type="button" className="btn-ghost text-xs" onClick={() => onTicket(a)}>New ticket</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AssetsPage() {
  const [params] = useSearchParams();
  const perms = useMe().data?.permissions ?? [];
  const [q, setQ] = useState(params.get("q") ?? "");
  const [coverage, setCoverage] = useState("");
  const [adding, setAdding] = useState(false);
  const [ticketFor, setTicketFor] = useState<Asset | null>(null);
  const assets = useAssets({ q: q || undefined, coverage: coverage || undefined });
  return (
    <>
      <PageHeader title="Installed base" subtitle="Every machine at your customers' sites, with warranty and AMC cover. Search a serial number to check cover."
        actions={can(perms, "service:create") && <button type="button" className="btn-primary" onClick={() => setAdding(true)}><Icon name="plus" />Add machine</button>} />
      <div className="mb-4 flex flex-wrap gap-2">
        <input type="search" className="input max-w-xs" aria-label="Search serial number or name" placeholder="Serial number or name" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="input w-auto" aria-label="Cover" value={coverage} onChange={(e) => setCoverage(e.target.value)}>
          <option value="">Any cover</option><option value="warranty">Under warranty</option><option value="amc">Under AMC</option><option value="chargeable">Chargeable</option></select>
      </div>
      {assets.isLoading ? <Spinner /> : !assets.data?.length ? <EmptyState title="No machines found" body="Add machines here, or from a delivered order with their serial numbers." /> : (
        <div className="card"><MachineTable items={assets.data} onTicket={can(perms, "service:create") ? setTicketFor : undefined} /></div>
      )}
      {adding && <AddMachine onClose={() => setAdding(false)} />}
      {ticketFor && <NewTicketDialog account={ticketFor.account} onClose={() => setTicketFor(null)} onCreated={() => setTicketFor(null)} />}
    </>
  );
}

/** On a delivered order: one machine per serial number goes into the customer's installed base. */
export function InstallDialog({ order, onClose }: { order: Order; onClose: () => void }) {
  const save = useCall<Asset[]>("assets", "order");
  const [serials, setSerials] = useState<Record<string, string>>({});
  const [months, setMonths] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<Asset[] | null>(null);
  const lines = Object.entries(serials).map(([line_id, text]) => ({ line_id, serial_numbers: text.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean) })).filter((l) => l.serial_numbers.length);
  return (
    <Modal title="Add to installed base" onClose={onClose}>
      {done ? <div className="space-y-3"><Alert tone="ok">{done.length} machine{done.length === 1 ? "" : "s"} added to {order.account?.name}'s installed base.</Alert>
        <div className="flex justify-end"><Link to={`/accounts/${order.account?.id}`} className="btn-primary">Open the customer</Link></div></div> : (
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); setError(null); save.mutateAsync({ path: `/orders/${order.id}/install`, body: { lines, ...(months ? { warranty_months: Number(months) } : {}) } }).then(setDone).catch((err) => setError(errorText(err))); }}>
          {error && <Alert>{error}</Alert>}
          <p className="text-sm text-muted">Enter the serial numbers that went out, one per line or separated by commas.</p>
          {order.lines.map((l) => (
            <div key={l.id}><label htmlFor={`sn-${l.id}`} className="label">{l.description} ({Number(l.quantity)} {l.uom})</label>
              <textarea id={`sn-${l.id}`} className="input min-h-16 font-mono text-xs" value={serials[l.id] ?? ""} onChange={(e) => setSerials({ ...serials, [l.id]: e.target.value })} /></div>
          ))}
          <div className="w-48"><label htmlFor="sn-months" className="label">Warranty (months)</label><input id="sn-months" className="input" type="number" min={0} placeholder="Company default" value={months} onChange={(e) => setMonths(e.target.value)} /></div>
          <div className="flex justify-end gap-2"><button type="button" className="btn-ghost" onClick={onClose}>Cancel</button><button type="submit" className="btn-primary" disabled={!lines.length || save.isPending}>Add machines</button></div>
        </form>
      )}
    </Modal>
  );
}

export { AddMachine };
